import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUser } from '../../lib/storage';
import { UserRoleEnums } from '../../utility/userRoles';
import { changeDocumentTitle, changeFavicon } from '../../utility/document';
import { ShowImage } from '../../utils/ShowImage';
import { fetchImageUrl } from '../../helpers/helper';

/**
 * Top Navigation Component
 * 
 * Modern top navigation bar for the Agentic AI platform
 * 
 * @returns {JSX.Element} Top navigation component
 */
export const TopNavigation: React.FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const user = getUser();
  const {user: authUser} = useAuth()

  const role = user?.user.role;
  const tenant = user?.tenant;

  useEffect(()=>{
    // console.log("authUser in TopNavigation==>",authUser)
  },[authUser])

  const fetchFaviconUrl = async ()=>{
   if(tenant?.s3FaviconObjectData){
    
    const url = await fetchImageUrl(tenant?.s3FaviconObjectData)
      if(url){
        changeFavicon(url);
      }
    }
  }
  useEffect(() => {
      fetchFaviconUrl()
      changeDocumentTitle(tenant?.name || process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform');
}, [tenant]);

  const navigationUserItems = [
    { name: 'Dashboard', href: '/dashboard', icon: '📊' },
    // { name: 'Agent Types', href: '/agent-types', icon: '🏷️' },
    { name: 'AI Agents', href: '/agents', icon: '🤖' },
    { name: 'Chatbots', href: '/chatbots', icon: '💬' },
    { name: 'Ecosystems', href: '/ecosystems', icon: '🌐' },
    { name: 'Workflows', href: '/workflows', icon: '⚡' },
    // { name: 'Settings', href: '/settings', icon: '⚙️' },
  ];

  const navigationSuperAdminItems = [
    { name: 'All Clients', href: '/tenant-listing', icon: '📊' },
    { name: 'Agent Type', href: '/super-agent-type', icon: '🏷️' },
    { name: 'MCP Catalog', href: '/super-mcp-catalog', icon: '🏷️'  },
  ];



  const navigationAdminItems = [
    { name: 'Dashboard', href: '/dashboard', icon: '📊' },
    // { name: 'Agent Types', href: '/agent-types', icon: '🏷️' },
    { name: 'AI Agents', href: '/agents', icon: '🤖' },
    { name: 'Chatbots', href: '/chatbots', icon: '💬' },
    { name: 'Ecosystems', href: '/ecosystems', icon: '🌐' },
    { name: 'Workflows', href: '/workflows', icon: '⚡' },
    // { name: 'Users', href: '/users', icon: '👤' },
    // { name: 'Departments', href: '/departments', icon: '🏢' },
    // { name: 'Settings', href: '/settings', icon: '⚙️' },
  ];


  const allItems = 
    role === UserRoleEnums.SUPER_ADMIN ? navigationSuperAdminItems : 
    role === UserRoleEnums.ADMIN ? navigationAdminItems :
    navigationUserItems;


  const isActive = (href: string) => {
    return location.pathname === href;
  };
  const {user:user1} = useAuth()

  useEffect(()=>{
    // console.log("user1==>",user1)
  },[user1])

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-8">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <ShowImage
                s3Params={tenant?.s3LogoObjectData}
                src={process.env.REACT_APP_APPLICATION_LOGO_URL || "agentic-logo.png"}
                alt={process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform'}
                className="h-12 w-auto"
                loading="eager"
                referrerPolicy="no-referrer"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900 capitalize">{tenant?.name}</h1>
                <p className="text-xs text-gray-500 -mt-1">Agentic Platform</p>
              </div>
            </Link>

            {/* Navigation Links */}
            <div className="hidden lg:flex space-x-1">
              {allItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                    isActive(item.href)
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            {/* <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h6v-6H4v6zM4 5h6V1H4v4zM15 7h5l-5-5v5z" />
              </svg>
            </button> */}

            {/* AI Status Indicator */}
            {/* <div className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-green-700">AI Active</span>
            </div> */}

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.firstName?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900 capitalize">{user?.firstName || 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.roles?.[0] || 'Admin'}</p>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 capitalize">{user?.firstName || 'User'}</p>
                    <p className="text-xs text-gray-500">{user?.email || 'user@example.com'}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    Profile Settings
                  </Link>
                  <Link
                    to="/preferences"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    Preferences
                  </Link>
                  <div className="border-t border-gray-100">
                    <button
                      onClick={() => {
                        logout();
                        setIsProfileOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden border-t border-gray-200">
        <div className="px-4 py-2">
          <div className="grid grid-cols-3 gap-1">
            {navigationUserItems.slice(0, 6).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`p-2 rounded-lg text-center transition-colors ${
                  isActive(item.href)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="text-lg mb-1">{item.icon}</div>
                <div className="text-xs font-medium">{item.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};
